// El rango de fechas que comparten todas las pantallas con período.
//
// Vive en `lib` y no adentro de una feature porque lo usan las cuatro solapas
// de reportes, el listado de ventas, la bitácora, la merma y las compras: los
// atajos y el formato tienen que ser los mismos en todas, o el usuario aprende
// cinco veces lo mismo.
//
// Estaba en `features/reportes/` y el listado de ventas tenía su PROPIA copia
// —el mismo comentario, su propio `iso`, su propio RANGOS—. Esa copia es lo
// que dejó que el bug de la zona del local sobreviviera a arreglarlo: los
// reportes ya tomaban el día del local y las ventas seguían tomando el del
// navegador. Hay un solo juego de rangos, y un test lo verifica.

import { zonaDelLocal } from '@/lib/formato'

/**
 * El día del calendario del LOCAL, no el del navegador.
 *
 * «Hoy» es una pregunta sobre el mostrador, y la responde la zona del local,
 * que baja en el login (`tenant.zonaHoraria`). Tomarla del navegador anda en
 * la máquina bien configurada y falla en la que importa: la PC del mostrador
 * que nadie configuró y quedó en UTC. Ahí, de las nueve de la noche a la
 * medianoche, «Hoy» pide el día de MAÑANA y el listado sale vacío —justo
 * cuando el local está cerrando la caja y mirando el total del día—. El
 * verdulero no concluye «el reloj está mal», concluye «el sistema perdió las
 * ventas de hoy».
 *
 * Es la misma regla que el backend ya aplica en `RangoLocal` y que
 * `formato.ts` aplica para MOSTRAR la hora; acá faltaba para ELEGIR el día.
 */
function enLaZonaDelLocal(d: Date): Date {
  const zona = zonaDelLocal()
  if (!zona) return d

  // en-CA da yyyy-mm-dd, que es justo lo que hace falta para reconstruir el
  // día del calendario del local sin pelearle al parseo.
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: zona,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)

  const [a, m, dia] = partes.split('-').map(Number)

  // Se devuelve como fecha LOCAL del navegador a mediodía: lo único que se le
  // pide de acá en adelante es su año, mes y día, y el mediodía deja que
  // sumar o restar días no cruce ningún borde por el horario de verano.
  return new Date(a ?? 0, (m ?? 1) - 1, dia ?? 1, 12)
}

/** yyyy-mm-dd de una fecha, tomando su año, mes y día tal como vienen. */
export function iso(d: Date): string {
  // Se arma a mano y no con toISOString: el ISO está en UTC y a la noche
  // devuelve el día de mañana, así que la pantalla arrancaría mostrando un
  // día sin ventas justo cuando el local está abierto.
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

/** Hoy en el local. */
export function hoy(): string {
  return iso(enLaZonaDelLocal(new Date()))
}

function corrida(dias: number): Date {
  const d = enLaZonaDelLocal(new Date())
  d.setDate(d.getDate() + dias)
  return d
}

export interface Rango {
  desde: string
  hasta: string
}

/**
 * Los rangos que se piden de verdad.
 *
 * Existen porque tipear dos fechas para ver «las de ayer» es trabajo de más en
 * la consulta más frecuente. El rango libre queda igual para el caso raro.
 */
export const RANGOS: { titulo: string; calcular: () => Rango }[] = [
  { titulo: 'Hoy', calcular: () => ({ desde: hoy(), hasta: hoy() }) },
  { titulo: 'Ayer', calcular: () => ({ desde: iso(corrida(-1)), hasta: iso(corrida(-1)) }) },
  {
    titulo: 'Últimos 7 días',
    calcular: () => ({ desde: iso(corrida(-6)), hasta: hoy() }),
  },
  {
    titulo: 'Este mes',
    calcular: () => {
      const d = enLaZonaDelLocal(new Date())
      return { desde: iso(new Date(d.getFullYear(), d.getMonth(), 1)), hasta: hoy() }
    },
  },
  {
    titulo: 'Mes pasado',
    calcular: () => {
      const d = enLaZonaDelLocal(new Date())
      // Día cero del mes actual es el último del anterior: evita tener que
      // saber si el mes tenía 28, 30 o 31.
      return {
        desde: iso(new Date(d.getFullYear(), d.getMonth() - 1, 1)),
        hasta: iso(new Date(d.getFullYear(), d.getMonth(), 0)),
      }
    },
  },
  {
    titulo: 'Últimos 12 meses',
    calcular: () => {
      const d = enLaZonaDelLocal(new Date())
      return { desde: iso(new Date(d.getFullYear() - 1, d.getMonth(), d.getDate())), hasta: hoy() }
    },
  },
]

/** El rango como query string para la API. */
export function comoParams(rango: Rango, extra?: Record<string, string | number | null>): string {
  const p = new URLSearchParams({ desde: rango.desde, hasta: rango.hasta })

  for (const [clave, valor] of Object.entries(extra ?? {})) {
    if (valor !== null && valor !== '') p.set(clave, String(valor))
  }

  return p.toString()
}

